// Route-level code splitting for the signed-in app.
//
// Until 2026-10-07 every page, including the public marketing pages, shipped
// the whole application in one 1.2 MB bundle: the dashboard, Ads Studio, the
// CMO, settings. A visitor reading /pricing downloaded all of it. Routes
// wrapped in lazyRoute() become their own chunk and load on first visit.
//
// Only routes that are NOT prerendered may use lazyRoute(). scripts/prerender.mjs
// renders with renderToString, which emits the Suspense fallback for a lazy
// component, so a lazily rendered public page would be written to HTML as an
// empty spinner; and in the browser, createRoot would replace the prerendered
// markup with that spinner until the chunk arrived.
//
// Public pages use publicPage() below. It is still a separate chunk, but one
// that can be fetched before rendering: once preload() has resolved, the page
// renders synchronously, with no Suspense boundary involved. Until 2026-10-13
// every public page was a static import, and a visitor reading one blog post
// downloaded all fifty, plus every guide and the landing page.

import {
  createElement,
  lazy,
  useState,
  type ComponentProps,
  type ComponentType,
  type LazyExoticComponent,
  type ReactElement,
} from "react";

const RELOAD_KEY = "mockingbird.chunkReloadAt";
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * A deploy replaces every hashed chunk. A tab opened before the deploy still
 * holds the old index, so its first navigation to a lazy route asks for a file
 * that no longer exists and the import rejects. Reloading fetches the new
 * index and fixes it. The timestamp stops a genuinely broken chunk from
 * reloading in a loop: a second failure inside the cooldown is thrown to the
 * error boundary instead.
 */
function recoverFromMissingChunk(error: unknown): Promise<never> {
  if (typeof window === "undefined") throw error;
  let lastReload = 0;
  try {
    lastReload = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {
    // Storage blocked: fall through and surface the error rather than risk a loop.
    throw error;
  }
  if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) throw error;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    throw error;
  }
  window.location.reload();
  // Never settles: the page is about to be replaced, and resolving or
  // rejecting would flash the fallback or the error screen first.
  return new Promise<never>(() => {});
}

/**
 * `lazyRoute(() => import("@/routes/dashboard").then((m) => m.Dashboard))`
 *
 * Takes the component itself rather than a module with a default export,
 * because route files here use named exports.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<T>,
): LazyExoticComponent<T> {
  return lazy(() =>
    load().then(
      (component) => ({ default: component }),
      (error: unknown) => recoverFromMissingChunk(error),
    ),
  );
}

/** A public page component that can be downloaded before it is rendered. */
export interface PreloadablePage<P> {
  (props: P): ReactElement;
  /** Fetches the chunk. Safe to call more than once. */
  preload: () => Promise<void>;
}

/**
 * `publicPage(() => import("@/routes/pricing").then((m) => m.Pricing))`
 *
 * Rendered after preload() has resolved, it returns the real component
 * directly. Rendered before (a client-side navigation to a page not yet
 * fetched), it falls back to React.lazy and the nearest Suspense boundary.
 *
 * Which of the two a given mount uses is fixed when it mounts. Switching from
 * the lazy wrapper to the real component on a later render would change the
 * element type and remount the whole page.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function publicPage<T extends ComponentType<any>>(
  load: () => Promise<T>,
): PreloadablePage<ComponentProps<T>> {
  let loaded: T | null = null;
  let pending: Promise<T> | null = null;
  const fetchOnce = (): Promise<T> => {
    pending ??= load().then(
      (component) => {
        loaded = component;
        return component;
      },
      (error: unknown) => {
        pending = null;
        return recoverFromMissingChunk(error);
      },
    );
    return pending;
  };
  const Lazy = lazy(() => fetchOnce().then((component) => ({ default: component })));

  function Page(props: ComponentProps<T>): ReactElement {
    const [Ready] = useState<T | null>(() => loaded);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return createElement((Ready ?? Lazy) as ComponentType<any>, props);
  }
  Page.preload = () => fetchOnce().then(() => undefined);
  return Page;
}
